import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export interface UIState {
  // Theme and appearance
  theme: 'light' | 'dark'
  
  // Layout
  sidebarOpen: boolean
  fullscreen: boolean
  
  // Modals and overlays
  showConfirmDialog: boolean
  confirmDialogProps: {
    title: string
    message: string
    confirmText: string
    cancelText: string
    onConfirm?: () => void
    onCancel?: () => void
  } | null
  
  // Notifications
  notifications: Array<{
    id: string
    type: 'info' | 'success' | 'warning' | 'error'
    title: string
    message: string
    timestamp: number
    duration?: number
  }>
  
  // Loading states for different UI sections
  loadingStates: {
    interview: boolean
    transcription: boolean
    upload: boolean
    report: boolean
  }
  
  // Video/Audio settings
  videoSettings: {
    enabled: boolean
    cameraPosition: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left'
    size: 'small' | 'medium' | 'large'
    minimized: boolean
  }
  
  audioSettings: {
    enabled: boolean
    microphoneLevel: number
    noiseCancellation: boolean
    echoCancellation: boolean
  }
  
  // Accessibility
  accessibility: {
    reducedMotion: boolean
    highContrast: boolean
    fontSize: 'small' | 'medium' | 'large'
    screenReaderEnabled: boolean
  }
  
  // Error handling
  lastError: {
    message: string
    timestamp: number
    stack?: string
  } | null
}

const initialState: UIState = {
  theme: 'light',
  
  sidebarOpen: false,
  fullscreen: false,
  
  showConfirmDialog: false,
  confirmDialogProps: null,
  
  notifications: [],
  
  loadingStates: {
    interview: false,
    transcription: false,
    upload: false,
    report: false,
  },
  
  videoSettings: {
    enabled: true,
    cameraPosition: 'top-right',
    size: 'medium',
    minimized: false,
  },
  
  audioSettings: {
    enabled: true,
    microphoneLevel: 0,
    noiseCancellation: true,
    echoCancellation: true,
  },
  
  accessibility: {
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    highContrast: window.matchMedia('(prefers-contrast: high)').matches,
    fontSize: 'medium',
    screenReaderEnabled: false,
  },
  
  lastError: null,
}

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Theme
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload
    },
    
    toggleTheme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light'
    },
    
    // Layout
    setSidebarOpen: (state, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload
    },
    
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen
    },
    
    setFullscreen: (state, action: PayloadAction<boolean>) => {
      state.fullscreen = action.payload
    },
    
    toggleFullscreen: (state) => {
      state.fullscreen = !state.fullscreen
    },
    
    // Confirm dialog
    showConfirmDialog: (state, action: PayloadAction<UIState['confirmDialogProps']>) => {
      state.showConfirmDialog = true
      state.confirmDialogProps = action.payload
    },
    
    hideConfirmDialog: (state) => {
      state.showConfirmDialog = false
      state.confirmDialogProps = null
    },
    
    // Notifications
    addNotification: (state, action: PayloadAction<Omit<UIState['notifications'][0], 'id' | 'timestamp'>>) => {
      const notification = {
        ...action.payload,
        id: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
      }
      state.notifications.push(notification)
    },
    
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(n => n.id !== action.payload)
    },
    
    clearNotifications: (state) => {
      state.notifications = []
    },
    
    // Loading states
    setLoadingState: (state, action: PayloadAction<{ key: keyof UIState['loadingStates']; value: boolean }>) => {
      state.loadingStates[action.payload.key] = action.payload.value
    },
    
    // Video settings
    updateVideoSettings: (state, action: PayloadAction<Partial<UIState['videoSettings']>>) => {
      state.videoSettings = { ...state.videoSettings, ...action.payload }
    },
    
    toggleVideoEnabled: (state) => {
      state.videoSettings.enabled = !state.videoSettings.enabled
    },
    
    toggleVideoMinimized: (state) => {
      state.videoSettings.minimized = !state.videoSettings.minimized
    },
    
    // Audio settings
    updateAudioSettings: (state, action: PayloadAction<Partial<UIState['audioSettings']>>) => {
      state.audioSettings = { ...state.audioSettings, ...action.payload }
    },
    
    setMicrophoneLevel: (state, action: PayloadAction<number>) => {
      state.audioSettings.microphoneLevel = action.payload
    },
    
    // Accessibility
    updateAccessibilitySettings: (state, action: PayloadAction<Partial<UIState['accessibility']>>) => {
      state.accessibility = { ...state.accessibility, ...action.payload }
    },
    
    // Error handling
    setLastError: (state, action: PayloadAction<{ message: string; stack?: string }>) => {
      state.lastError = {
        ...action.payload,
        timestamp: Date.now(),
      }
    },
    
    clearLastError: (state) => {
      state.lastError = null
    },
  },
})

export const {
  setTheme,
  toggleTheme,
  setSidebarOpen,
  toggleSidebar,
  setFullscreen,
  toggleFullscreen,
  showConfirmDialog,
  hideConfirmDialog,
  addNotification,
  removeNotification,
  clearNotifications,
  setLoadingState,
  updateVideoSettings,
  toggleVideoEnabled,
  toggleVideoMinimized,
  updateAudioSettings,
  setMicrophoneLevel,
  updateAccessibilitySettings,
  setLastError,
  clearLastError,
} = uiSlice.actions

export default uiSlice.reducer

// Selectors
export const selectUI = (state: { ui: UIState }) => state.ui
export const selectTheme = (state: { ui: UIState }) => state.ui.theme
export const selectSidebarOpen = (state: { ui: UIState }) => state.ui.sidebarOpen
export const selectNotifications = (state: { ui: UIState }) => state.ui.notifications
export const selectLoadingStates = (state: { ui: UIState }) => state.ui.loadingStates
export const selectVideoSettings = (state: { ui: UIState }) => state.ui.videoSettings
export const selectAudioSettings = (state: { ui: UIState }) => state.ui.audioSettings
export const selectAccessibilitySettings = (state: { ui: UIState }) => state.ui.accessibility